import { MatchTargetKey } from '../models/source-key.model'
import { MATCH_COLUMN_PAIRS } from '../models/transactions-query.model'
import { WorkbenchRow } from '../models/workbench-row.model'

/**
 * Interpretation of the read API's per-counterpart match columns (helios !499, PVAL-26557).
 *
 * Each row carries one count + id-list pair per counterpart of its side — see
 * {@link MATCH_COLUMN_PAIRS}:
 *
 *     Enfusion row:   custodyMatches: 2, custodyMatchesIds: ["11355519885", "11355519886"],
 *                     tradingMatches: 0, tradingMatchesIds: [], clearwaterMatches: 0, ...
 *     Prometheus row: enfusionMatches: 1, enfusionMatchesIds: ["1316698909"], clearwaterMatches: 0, ...
 *
 * They replace the `matchedIds` / `matchCounts` JSON blobs, which no longer exist. Every pair is
 * written on every hydrate, so a counterpart with no matches is an explicit `0` / `[]` rather than an
 * absent key, and the server derives each count from its list, so the two cannot disagree. Both are
 * requested on every fetch (`REQUIRED_FIELDS`) and carried through raw by `row.adapter.ts`. Status is
 * unaffected: it comes from `matchStatus`.
 */

/** Normalised, per-row match facts as the server reported them. */
export interface ServerMatchInfo {
  /** Ids this row is matched to, across every counterpart. */
  matchedIds: string[]
  /** Per-target counts the server reported. */
  counts: Partial<Record<MatchTargetKey, number>>
}

export const emptyMatchInfo = (): ServerMatchInfo => ({
  matchedIds: [],
  counts: {},
})

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** An id is only useful as a string; numeric ids arrive as numbers on the Enfusion side. */
const idOf = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value.trim() || undefined
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return undefined
}

/**
 * Read one id-list column into a flat, de-duplicated id list.
 *
 * The contract is a JSON array of strings. The reader also tolerates a JSON string wrapping one, a
 * comma-separated string, and nesting — cheap, and it means a serialisation change degrades to the
 * same ids rather than to none.
 */
export function readMatchedIds(raw: unknown): string[] {
  const collected: string[] = []

  const walk = (value: unknown, depth: number): void => {
    if (value == null || depth > 3) return
    if (Array.isArray(value)) {
      value.forEach(entry => walk(entry, depth + 1))
      return
    }
    if (isRecord(value)) {
      Object.values(value).forEach(entry => walk(entry, depth + 1))
      return
    }
    if (typeof value === 'string') {
      const text = value.trim()
      if (!text) return
      if (text.startsWith('[') || text.startsWith('{')) {
        try {
          walk(JSON.parse(text), depth + 1)
          return
        } catch {
          // Not JSON after all — fall through to the delimited reading below.
        }
      }
      text.split(',').forEach(part => {
        const id = idOf(part)
        if (id) collected.push(id)
      })
      return
    }
    const id = idOf(value)
    if (id) collected.push(id)
  }

  walk(raw, 0)
  return [...new Set(collected)]
}

/**
 * Coerce one count column to a usable count.
 *
 * A match count is a whole number of transactions, so a fractional or negative value can only mean
 * a malformed payload; drop it rather than render it in a column as if it were server truth. An
 * empty string is dropped too — `Number('')` is `0`, which would claim "no matches" where the
 * server actually said nothing.
 */
const countOf = (value: unknown): number | undefined => {
  let count: unknown = value
  if (typeof count === 'string') {
    const text = count.trim()
    if (!text) return undefined
    count = Number(text)
  }
  if (typeof count !== 'number') return undefined
  // Number.isInteger already excludes NaN and Infinity.
  if (!Number.isInteger(count) || count < 0) return undefined
  return count
}

/** Every counterpart pair either side can carry, de-duplicated (clearwater is on both). */
const ALL_PAIRS = [
  ...MATCH_COLUMN_PAIRS.api,
  ...MATCH_COLUMN_PAIRS.prom,
].filter(
  (pair, index, pairs) =>
    pairs.findIndex(other => other.count === pair.count) === index,
)

/**
 * Read one row's server match facts out of its raw field values.
 *
 * Reads every pair either side can carry and takes what the row has, so the reader does not need to
 * know which source the row came from. The id lists are flattened into one — the panel resolves each
 * id's own origin from the loaded rows. A count is taken only from its count column; when that is
 * absent or malformed the target is left out and `MatchingService.matchCount` derives the count from
 * the loaded rows instead (both columns are always requested, so this is a degraded path only).
 */
export function readServerMatchInfo(row: WorkbenchRow): ServerMatchInfo {
  const matchedIds: string[] = []
  const counts: Partial<Record<MatchTargetKey, number>> = {}

  ALL_PAIRS.forEach(({ target, count, ids }) => {
    matchedIds.push(...readMatchedIds(row.fields[ids]))
    const reported = countOf(row.fields[count])
    if (reported !== undefined) counts[target] = reported
  })

  return { matchedIds: [...new Set(matchedIds)], counts }
}
